import { RefObject, useRef } from 'react';
import { MenuItem } from '@szhsin/react-menu';
import styled from 'styled-components';
import {
  useUnifiedOrdersFetchData,
  useOrdersIdsList,
  useUnifiedOrdersService,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-orders';
import { isDrfqId } from '@paradigm/features/src/unified-rfqs/entities/rfq';
import ActionsBarEmpty from '#/components/UnifiedRfqs/components/bottom-tabs/ActionsBarEmpty';
import {
  TrackingCtxProvider,
  useTrackedCallback,
} from '@paradigm/features/src/analytics';
import CreateMarketMenuItem from '#/components/UnifiedRfqs/context-menu/CreateMarketMenuItem';
import BaseTable from '../components/BaseTable';
import ContextMenuProvider, {
  ContextMenu as BaseDrfqContextMenu,
  useContextMenuActions,
  useContextMenuView,
} from '../context-menu/context-menu';
import OrderHistoryRow, { OrderHistoryHeaderRow } from './OrdersHistoryRow';
import OrderDetailsModal from './OrderDetailsModal';
import type { Order } from '@paradigm/features/src/unified-rfqs/entities/shared';

export default function OrdersHistoryTable() {
  const { status, canFetchMore } = useUnifiedOrdersFetchData();
  const { fetcher } = useUnifiedOrdersService();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const orderIds = useOrdersIdsList();

  return (
    <TrackingCtxProvider value="order history tab">
      <Container>
        <ActionsBarEmpty />
        <BaseTable
          status={status}
          canFetchMore={canFetchMore}
          fetcher={fetcher}
          isEmpty={orderIds.length === 0}
          entityRowsList={
            <OrdersRowsList orderIds={orderIds} scrollableRef={scrollableRef} />
          }
          headerRow={<OrderHistoryHeaderRow />}
          dataTestId="unified-rfq-order-history-table"
          scrollableRef={scrollableRef}
          emptyListMessage="No orders happened today"
        />
      </Container>
    </TrackingCtxProvider>
  );
}

const Container = styled.div`
  flex: 1; // take 100% heigh
  display: flex;
  flex-direction: column;
`;

function ContextMenu() {
  const { createRfq, openDetailsModal } = useContextMenuActions();
  const { rfqId } = useContextMenuView();
  const datadogPrefix = 'Orders History Table Context Menu';
  const handleCreateRfq = useTrackedCallback(
    'create market',
    () => createRfq('RFQ'),
    {
      auctionType: 'RFQ',
    },
  );
  const handleCreateOb = useTrackedCallback(
    'create market',
    () => createRfq('OB'),
    {
      auctionType: 'OB',
    },
  );
  const handleViewDetails = useTrackedCallback(
    'view trade recap',
    openDetailsModal,
  );

  return (
    <BaseDrfqContextMenu>
      <CreateMarketMenuItem
        type="RFQ"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateRfq}
      />
      <CreateMarketMenuItem
        type="OB"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateOb}
      />

      {/* For now we just display view details for DRFQs */}
      {rfqId != null && isDrfqId(rfqId) && (
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
      )}
    </BaseDrfqContextMenu>
  );
}

function OrdersRowsList(props: {
  scrollableRef: RefObject<HTMLDivElement>;
  orderIds: readonly Order['id'][];
}) {
  return (
    <ContextMenuProvider>
      {props.orderIds.map((id) => (
        <OrderHistoryRow
          key={id}
          orderId={id}
          scrollableRef={props.scrollableRef}
        />
      ))}
      <ContextMenu />
      <OrderDetailsModal />
    </ContextMenuProvider>
  );
}
